import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { defaultAvocado } from "../../../../../actions/Avocados/constants";
import { createDeviceEvent, pollCheckDevice } from "../../../../../actions/Devices/actions";
import { DeviceEventType } from "../../../../../actions/Devices/constants";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { useCreateManualAvocadoMutation, useDuplicateAvocadoMutation, useTriggerCheckBusinessRulesMutation } from "../../../../../hooks/mutations";
import { checkQueryKey, useAppendFruitToCheckCache } from "../../../../../hooks/mutations/usePatchCheckMutation";
import { useConnectivity } from "../../../../../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../../../../../offline/db";
import { addFruit as addFruitToSession, duplicateFruit as duplicateFruitInSession, getActiveSessionId } from "../../../../../offline/sessionManager";
import { RootState } from "../../../../../reducers";
import { toast } from "../../../../../utils/toast";
import { useEffectOnce } from "../../../../../utils/useEffectOnce";
import { Loading } from "../../../../Helper/Loading";
import { useQcEntities } from "../../QcEntityContext";
import useCheckFlowNavigate from "../../useCheckFlowNavigate";
import Box from "../Layout";
import { BulkCopyPasteFormModal } from "./BulkCopyPasteFormModal";
import { ManualForm } from "./ManualForm";
import { ManualNavButtons } from "./ManualNavButtons";

import { useRouteCheckQuery } from "../../../../../hooks/useCurrentEntities";

export const listenForProcessingEventChange = (device_uid: string | undefined, check: any | undefined) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const processing = useSelector<RootState, any>((state) => state.devices.queue);
    const use_fta_device = check?.use_fta_device && device_uid && check?.check_id;

    const processedFruits = processing.reduce((acc, curr) => [
        ...acc,
        ...curr?.data?.updated_fruits || [],
    ], []);

    useEffect(() => {
        let interval;

        if (use_fta_device) {
            dispatch(pollCheckDevice(device_uid, check.check_id));
            interval = setInterval(() => {
                dispatch(pollCheckDevice(device_uid, check.check_id));
            }, 750);
        }

        return interval !== undefined ? () => clearInterval(interval) : undefined;
    }, [use_fta_device]);

    useEffect(() => {
        if (use_fta_device) {
            queryClient.invalidateQueries({ queryKey: checkQueryKey(check) });
        }
    }, [processedFruits.length, use_fta_device]);
};

const ManualFruitForm = ({ type = "primary" }: { type?: "primary" | "secondary" }) => {
    const params: any = useParams();
    const tenantId = params.tenant_id as string;
    const { check, layer } = useQcEntities();
    const isLoading = useRouteCheckQuery().isLoading;
    // No mutation dispatches avocados.isUpdating anymore; a pending fruit mutation is the equivalent signal.
    const fruitLoading = useIsMutating() > 0;
    const config = useConfig();
    const location = config.get_check_type_config(layer, check);
    const checkLocationUnit = config?.get_check_unit_of_account(layer, check);

    const dispatch = useDispatch();
    const createManualAvocadoMutation = useCreateManualAvocadoMutation();
    const duplicateAvocadoMutation = useDuplicateAvocadoMutation();
    const triggerBusinessRulesMutation = useTriggerCheckBusinessRulesMutation();
    const appendFruitToCheckCache = useAppendFruitToCheckCache();
    const { navigateToCheckAction } = useCheckFlowNavigate();
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;

    // #region fta device related code
    const selected_fta = useSelector<RootState, any>((state) => state.devices.selected_fta);
    const device_id : string | undefined = selected_fta?.UID;
    const [ftaTriggerState, setFtaTriggerState] = useState(false);
    const useFtaDevice = check?.use_fta_device && device_id && check?.check_id !== undefined;

    const useGrid = (check?.use_bulk_edit || 0) > 1;
    const useCopyPasteModal = (useGrid && !!location?.bulk_input_modal && !useFtaDevice);
    const [bulkCopyPasteModalOpen, toggleBulkCopyPasteModal] = useState(!!useCopyPasteModal);


    listenForProcessingEventChange(device_id, check);

    const isThereAnyPressureFilled = check.avocados.some((avocado) => avocado.pressure);

    useEffect(() => {
        const sample_size = check?.sample_size || 1;
        const triggerBulkScan = sample_size > 1 && useFtaDevice && !ftaTriggerState && !isThereAnyPressureFilled;

        if (triggerBulkScan) {
            dispatch(createDeviceEvent({
                check_test_id: check.check_id,
                device_id,
                command: DeviceEventType.BULK_TRIGGER,
                fruit_ids: check.avocados.map((avocado) => avocado.avocado_id),
            }));

            setFtaTriggerState(true);
        }
    }, [ftaTriggerState, useFtaDevice]);
    // #endregion

    let avocado = { avocado_id: null, defects: [] };
    if (params.avocado_id > 0) {
        avocado = check.avocados.find((i) => i.avocado_id === parseInt(params.avocado_id, 10)) || { avocado_id: null, defects: [] };
    } else if (check.avocados && check.avocados.length > 0) {
        avocado = check.avocados[check.avocados.length - 1];
    }

    /**
     * Offline branch (issue 5.4). Skips the createManualAvocado axios
     * action and instead grows the offline session in IndexedDB, then writes
     * the synthetic fruit into the check cache — an offline check has no
     * server row, so there is nothing to refetch. The only differences from
     * the online success: temp_avocado_id placeholder (Date.now()-based, won't
     * collide with server ids) and the fruit's idempotency_key being persisted
     * in the session for eventual replay.
     */
    const addFruitOffline = async (n: number) => {
        const sessionId = getActiveSessionId();
        if (!sessionId) {
            toast.error("No active offline session — please return to the scanner.");
            return;
        }
        // Sequential by design: addFruitToSession reads + writes the
        // session row, so concurrent calls would race on the read-modify-
        // write of session.fruits. n is small (typically 1, occasionally
        // bulk_edit_size) so the serial cost is negligible.
        // eslint-disable-next-line no-await-in-loop
        for (let i = 0; i < n; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const { temp_avocado_id } = await addFruitToSession(sessionId, checkLocationUnit);
            const syntheticFruit = {
                ...defaultAvocado,
                avocado_id: temp_avocado_id,
                check_id: check.check_id,
                tenant_id: check.tenant_id,
                unit_of_account: checkLocationUnit,
                defects: [],
                images: [],
                meta: [],
                // Fruit tables drop rows where is_invalid !== false; the server
                // always sets it, defaultAvocado does not.
                is_invalid: false,
            };
            appendFruitToCheckCache(check, syntheticFruit);
        }
    };

    const addFruit = (n = 1) => {
        if (inOfflineMode) {
            return addFruitOffline(n);
        }
        return createManualAvocadoMutation.mutateAsync({
            tenantId,
            checkId: check.check_id,
            data: { n, unit_of_account: checkLocationUnit },
        }).then(() => {
            if (n > 1) {
                triggerBusinessRulesMutation.mutate({ check });
            }
        });
    };

    /**
     * Offline branch of duplicateFruit. The online POST /avocados/{id}/duplicate/
     * would be paused by TanStack (and 404 on reconnect, since the source only
     * has a temp id), so copy the fruit inside the offline session instead and
     * append a synthetic copy of the cached source fruit — it already carries
     * the values typed so far — to the check cache. The navigate-to-latest
     * effect below then opens the copy, as it does after an online duplicate.
     */
    const duplicateFruitOffline = async (source: any) => {
        const sessionId = getActiveSessionId();
        if (!sessionId) {
            toast.error("No active offline session — please return to the scanner.");
            return;
        }
        try {
            const { temp_avocado_id } = await duplicateFruitInSession(sessionId, source.avocado_id);
            appendFruitToCheckCache(check, {
                ...source,
                avocado_id: temp_avocado_id,
                defects: [...(source.defects ?? [])],
                // Photos are not duplicated (the backend doesn't copy them either).
                images: [],
            });
        } catch {
            toast.error("Could not duplicate this fruit offline.");
        }
    };

    const duplicateFruit = () => {
        if (check.avocados.length === 0 || !avocado?.avocado_id) {
            return;
        }

        if (inOfflineMode) {
            duplicateFruitOffline(avocado);
            return;
        }

        duplicateAvocadoMutation.mutateAsync({
            tenantId,
            avocadoId: avocado.avocado_id,
        });
    };

    // create a new fruit if there is no fruit yet
    useEffectOnce(() => {
        if (avocado.avocado_id === null && check.avocados.length === 0 && !fruitLoading && !useFtaDevice) {
            addFruit(check?.use_bulk_edit || 1);
        }
    });

    // navigate to latest fruit
    useEffect(() => {
        if (check.avocados.length > 0) {
            const { avocado_id } = check.avocados[check.avocados.length - 1];
            navigateToCheckAction(`${params.action}/${avocado_id}`, true, {}, true);
        }
    }, [check.avocados.length]);

    const buttons = <ManualNavButtons
        addFruit={addFruit}
        duplicateFruit={duplicateFruit}
        avocado={avocado}
        useGrid={useGrid}
        useCopyPasteModal={useCopyPasteModal}
        toggleBulkCopyPasteModal={toggleBulkCopyPasteModal}
    />;

    return (
        <Box hideButtons={isLoading} buttons={buttons}>
            {isLoading ? (
                <Loading />
            ) : (
                <>
                    {useGrid ? (
                        <>
                            <div className="pt-3">
                                {check.avocados.map((a) => (
                                    <div key={a.avocado_id}>
                                        <ManualForm avocado={a} grid={true} type={type} />
                                    </div>
                                ))}
                            </div>

                            {useCopyPasteModal && (
                                <BulkCopyPasteFormModal
                                    isOpen={bulkCopyPasteModalOpen}
                                    field={location?.bulk_input_modal as string}
                                    toggleModal={toggleBulkCopyPasteModal}
                                />
                            )}
                        </>
                    ) : (
                        <div className="pt-3">
                            <ManualForm grid={false} avocado={avocado} type={type} />
                        </div>
                    )}
                </>
            )}
        </Box>
    );
};

export default ManualFruitForm;
